/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from "react";
import HelperAccordion from "../../components/helperAccordion";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { useFontSize } from "../../components/contexts/AccessibilityContext";
import { fontSizeMap } from "../../utils/accessibilitySettings";
import OpportunityList from "../../components/OpportunityList";
import SideBar from "../../components/Sidebar";
import { FETCH_ALL_JOBS, FETCH_RECOMMENDED_JOBS } from "../../utils/constants";
import { makeAuthenticatedGet } from "../../utils/fetchUtils";
import Form from "react-bootstrap/Form";
import useWindowWidth from "../../utils/windowWidth";
import { Spinner } from "react-bootstrap";
import GuestLoginPrompt from "../../components/GuestLoginPrompt";
import { clearGuestResumeSession, getGuestResumeStatus } from "../../utils/guestSession";

function Opportunity() {
  const { fontSize } = useFontSize();

  const [displayStatus, setDisplayStatus] = useState<"all" | "recommend">(
    "all"
  );

  //this is to toggle the header for mobile interface
  const [govt,setGovt] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [jobs, setJobs] = useState<any[]>([]);
  const [pageJobs, setPageJobs] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("corporate_job");
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [resumeUploadRequired, setResumeUploadRequired] = useState(false);

  const onPageChange = (newPage: any) => {
    if (newPage < 1 || newPage > totalPages) return; // Prevent going out of bounds
    setPage(newPage);
  };

  const navigate = useNavigate();

  const mainRef = useRef<HTMLHeadingElement>(null);
  const location = useLocation();

  function handleSearchText(event: any): void {
    setSearchText(event.target.value);
  }

  const handleCategoryChange = (category: any) => {
    setSelectedCategory(category);
    fetchAllJobs(category);
    setPage(1);
    // if(category === 'government_job'){
    //   setGovt(true);
    // } else {
      setGovt(false);
    // }
  };

  function handleSearchSubmit(event: any): void {
    event.preventDefault();
    const query = searchText.trim().toLowerCase();
    if (!query) {
      setJobs(pageJobs);
      return;
    }
    const searchJobs = pageJobs.filter((job) => {
      return (
        job.company?.toLowerCase().includes(query) ||
        job.title?.toLowerCase().includes(query) ||
        job.resposibilites?.toLowerCase().includes(query) ||
        job.address?.toLowerCase().includes(query)
      );
    });
    setJobs(searchJobs);
  }

  const handleLinkClick = () => {
    // setActiveLink("File Upload");
    navigate("/opportunity/new");
  };

  useEffect(() => {
    fetchAllJobs(selectedCategory);
    // setSearchText("");
    document.title = "Jobs Listing - Nclude Portal";
  }, []);

  useEffect(() => {
    fetchAllJobs(selectedCategory);
  }, [page]);

  useEffect(() => {
    // Set focus on the main content when the route changes
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  function shuffleArray(array: any[]) {
    return array
      .map(value => ({ value, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map(({ value }) => value);
  }
  async function fetchAllJobs(categoryParam = ""): Promise<void> {
    setLoading(true);
    setLoadError("");
    setResumeUploadRequired(false);
    setSearchText("");
    setDisplayStatus("all");
    const category = categoryParam || selectedCategory;
    try {
      const country='usa';
      let url = FETCH_ALL_JOBS + `?country=${country}&page=${page}&limit=${limit}`;
      if (category) {
        url += `&job_category=${category}`;
      }
      const response = await makeAuthenticatedGet(url);
      const temp = response.jobs.map((job: any) => ({
          ...job,
          markAsRead: false,
      }));
      const shuffledJobs = shuffleArray(temp);
      setJobs(shuffledJobs);
      setPageJobs(shuffledJobs);
      setTotalPages(Math.floor((response.totalJobCount - 1) / limit) + 1);
    } catch (error: any) {
      console.error("Error fetching jobs: ", error);
      setLoadError("We couldn’t load jobs right now. Please try again.");
      setJobs([]);
      setPageJobs([]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchRecommendedJobs(): Promise<void> {
    const isGuest = !localStorage.getItem("token");
    if (isGuest) {
      const guestResumeStatus = getGuestResumeStatus();
      if (guestResumeStatus !== "valid") {
        setLoading(false);
        setDisplayStatus("recommend");
        setResumeUploadRequired(true);
        setLoadError(guestResumeStatus === "expired"
          ? "Your temporary resume information has expired. Upload your resume again to view personalized job recommendations."
          : "Upload your resume to view personalized job recommendations.");
        return;
      }
    } else if (localStorage.getItem("resume") === "false") {
      navigate("/resumeforjobs");
      return;
    }
    setLoading(true);
    setLoadError("");
    setResumeUploadRequired(false);
    setSearchText("");
    setDisplayStatus("recommend");
    try {
      const country='usa';
      const url = FETCH_RECOMMENDED_JOBS + `/${country}/${selectedCategory}` + `?country=${country}&page=${page}&limit=${limit}`;
      const response = await makeAuthenticatedGet(url);
      const temp = response.map((job: any) => ({
          ...job,
          markAsRead: false,
      }));
      setJobs(temp);
      setPageJobs(temp);
      setTotalPages(Math.floor((temp.length - 1) / limit) + 1);
    } catch (error: any) {
      console.error("Error fetching jobs: ", error);
      if (isGuest && error?.response?.status === 404) {
        clearGuestResumeSession();
        setResumeUploadRequired(true);
        setLoadError("Your temporary resume information is unavailable or has expired. Upload your resume again to view personalized job recommendations.");
      } else {
        setLoadError("We couldn’t load recommended jobs right now. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleRecommendCheck() {
    setIsChecked(!isChecked);
  }

  useEffect(() => {
    if (isChecked) {
      fetchRecommendedJobs();
    } else {
      fetchAllJobs();
    }
  }, [isChecked]);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  return (
    <div className="containerr">
      <GuestLoginPrompt serviceName="Jobs Listing" promptKey="jobs-listing" />
      {!isMobile && <Header
        activePage="jobs"
        curr={0}
        changeCategory={handleCategoryChange}
      />}
      <div className="page-container">
        <SideBar activePage="Jobs" />
        <main className="doc-container jobs-page" id="main-content" tabIndex={-1}>
          <div>
            <h1
              className="display-title"
              ref={mainRef}
              tabIndex={-1}
            >
              {"Jobs Listing"}
            </h1>
          </div>
          <p className="jobs-intro">Browse current opportunities and expand any listing for full role details.</p>
          <div className="d-flex items-center">
            <div className="search-div">
              <form
                className="d-flex search-form"
                role="search"
                onSubmit={handleSearchSubmit}
              >
                <label htmlFor="job-search" className="visually-hidden">Search jobs</label>
                <input
                  id="job-search"
                  className="form-control me-2 search-input job-search"
                  type="search"
                  placeholder="Search jobs by company, location, responsibilities..."
                  aria-label="Search jobs by company, location, responsibilities"
                  aria-describedby="job-search-help"
                  value={searchText}
                  onChange={handleSearchText}
                />
                <button
                  className="btn search-btn job-search-btn"
                  type="submit"
                  aria-label="search jobs"
                >
                  Search
                </button>
              </form>
              <p className="search-help" id="job-search-help">Searches the jobs shown on this page.</p>
            </div>
          </div>
          <p className="results-summary" role="status" aria-live="polite">
            {!loading && !loadError ? `${jobs.length} ${jobs.length === 1 ? "job" : "jobs"} shown` : ""}
          </p>
          <div className="component-container">
            {/* Toggle on right */}
            {/* {selectedCategory === "corporate_job" && (
              <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
                <label htmlFor="recommended-job">Recommended Jobs</label>

                <Form.Check
                  type="switch"
                  id="recommended-job"
                  checked={isChecked}
                  onChange={handleRecommendCheck}
                  role="switch"
                  aria-label="Toggle recommended jobs"
                />
              </div>
            )} */}
            {loadError ? (
              <div className="jobs-error" role="alert">
                <p>{loadError}</p>
                {resumeUploadRequired ? (
                  <button type="button" onClick={() => navigate("/opportunity/new")}>Upload resume</button>
                ) : (
                  <button type="button" onClick={() => fetchAllJobs(selectedCategory)}>Try again</button>
                )}
              </div>
            ) : loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px", minWidth: "100%" }}>
                <Spinner
                  animation="border"
                  role="status"
                  variant="primary"
                  style={{ width: "4rem", height: "4rem" }}
                >
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <OpportunityList
                jobs={jobs}
                page={page}
                totalPages={totalPages}
                setPage={setPage}
                onPageChange={onPageChange}
                selectedCategory={selectedCategory}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Opportunity;
